import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CalendarPlus, TriangleAlert } from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { abrirPeriodos, fetchPeriodos } from "@/routes/masters/api"
import { fetchSociedades } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

/**
 * LOS PERÍODOS CONTABLES.
 *
 * POR QUE ESTA PANTALLA EXISTE. Un asiento vive en un periodo, y `postear`
 * exige que ese periodo ESTE y este abierto. Un comprobante cuyo periodo no
 * esta abierto se clasifica, se trata, y **se detiene antes de llegar al
 * libro**. No se pierde y no falla: lo que se ve son dos sintomas que no
 * parecen tener relacion —un balance en cero y la cola de revision llena— y
 * hasta hoy la unica forma de resolverlo era un script de linea de comandos.
 *
 * LOS QUE FALTAN SALEN DE LOS COMPROBANTES. No de un rango que alguien
 * escriba: son los meses en los que hay documentos cargados sin periodo donde
 * asentarse. Convertir esa lista en un boton es la diferencia entre «el balance
 * esta raro» y «faltan estos seis periodos».
 *
 * ABRIR NO ES REABRIR. Un periodo cerrado se cerro por una razon —se presento,
 * se firmo— y esta pantalla nunca lo toca. Si un comprobante cae ahi, la
 * correccion va por asiento de reversion en un periodo posterior.
 */
export function PeriodosPage() {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const q = useQuery({ queryKey: ["periodos"], queryFn: () => fetchPeriodos() })
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })

  const faltantes = q.data?.faltantes ?? []
  const periodos = q.data?.periodos ?? []

  return (
    <div className="space-y-3">
      {faltantes.length > 0 ? (
        <Faltan faltantes={faltantes} puedeEditar={puedeEditar} />
      ) : null}

      {/* Cesar, 2026-10-04: «explícame lo de períodos de una manera más simple para el que no entiende nada». */}
      <p className="max-w-[80ch] rounded-md border border-dashed px-2.5 py-2 text-xs leading-5 text-muted-foreground">
        <strong className="text-foreground">{t("Un período es un mes del cuaderno contable de una sociedad.")}</strong>{" "}
        {t("Marty sólo puede asentar un comprobante en un mes que esté abierto. Si llega una factura de septiembre y septiembre no está abierto, la factura se clasifica pero no entra al libro, y el balance sale en cero sin ningún error. Por eso los meses con comprobantes esperando se abren aquí con un clic, y cada mes se cierra al firmar su declaración.")}
      </p>

      {puedeEditar ? <Abrir sociedades={sociedades.data ?? []} /> : null}

      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && periodos.length === 0}
        emptyMessage={t("Ninguna sociedad tiene períodos abiertos.")}
        emptyHint={t("Un asiento vive en un período: sin ninguno abierto, los comprobantes se clasifican y no llegan al libro. Ábrelos arriba.")}
        onRetry={() => void q.refetch()}
        columns={[26, 14, 14, 16, 16, 14]}
      >
        <WorkPanel
          title={t("Períodos")}
          meta={<Badge variant="outline">{periodos.length}</Badge>}
          footnote={q.data?.nota}
        >
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[18rem]">{t("Sociedad")}</TableHead>
                <TableHead className="w-24">{t("Período")}</TableHead>
                <TableHead className="w-28">{t("Estado")}</TableHead>
                <TableHead className="w-28 text-right">{t("Comprobantes")}</TableHead>
                <TableHead className="w-24 text-right">{t("Asientos")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {periodos.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="truncate" title={p.razon_social}>
                    {p.razon_social}
                  </TableCell>
                  <TableCell className="code">{p.periodo}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        p.estado === "abierto"
                          ? "outline"
                          : p.estado === "cerrado" || p.estado === "presentado"
                            ? "success-soft"
                            : "warning-soft"
                      }
                      estado
                    >
                      {p.estado}
                    </Badge>
                  </TableCell>
                  <TableCell className={cn("num", p.comprobantes === 0 && "num-zero")}>
                    {p.comprobantes}
                  </TableCell>
                  <TableCell className={cn("num", p.asientos === 0 && "num-zero")}>
                    {p.asientos}
                  </TableCell>
                  <TableCell>
                    {/*
                      «Abierto» no significa «usado». Un período abierto con
                      comprobantes y sin un solo asiento es exactamente el que
                      hay que mirar, y a simple vista los dos números se parecen.
                    */}
                    {p.comprobantes > 0 && p.asientos === 0 ? (
                      <span className="inline-flex items-center gap-1 text-2xs text-warning">
                        <TriangleAlert className="h-3 w-3" />
                        {p.comprobantes}{" "}{t("sin asentar")}</span>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      </DataState>
    </div>
  )
}

function Faltan({
  faltantes,
  puedeEditar,
}: {
  faltantes: { entity_id: string; razon_social: string; periodo: string; comprobantes: number }[]
  puedeEditar: boolean
}) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const porSociedad = new Map<string, { nombre: string; meses: string[]; docs: number }>()
  for (const f of faltantes) {
    const actual = porSociedad.get(f.entity_id) ?? {
      nombre: f.razon_social,
      meses: [],
      docs: 0,
    }
    actual.meses.push(f.periodo)
    actual.docs += f.comprobantes
    porSociedad.set(f.entity_id, actual)
  }

  const abrir = useMutation({
    mutationFn: (entityId: string) =>
      abrirPeriodos({
        entity_id: entityId,
        solo_faltantes: true,
        motivo: "Abrir los períodos que el histórico cargado necesita.",
      }),
    onSuccess: (r) => {
      toast({
        title: `${r.abiertos} período(s) abiertos`,
        description: r.nota ?? undefined,
      })
      void qc.invalidateQueries({ queryKey: ["periodos"] })
      void qc.invalidateQueries({ queryKey: ["primeros-pasos"] })
    },
    onError: onWriteError("los períodos", "abrir"),
  })

  return (
    <WorkPanel
      title={t("Comprobantes sin período donde asentarse")}
      meta={
        <Badge variant="warning-soft" estado>
          {faltantes.reduce((s, f) => s + f.comprobantes, 0)} comprobantes
        </Badge>
      }
      footnote={t("Estos meses tienen comprobantes cargados y ningún período abierto. Hasta que se abran, esos comprobantes se clasifican y NO llegan al libro: el balance sale en cero y la cola de revisión llena, que son dos síntomas que no parecen tener relación.")}
      bodyClassName="p-2.5"
    >
      <ul className="space-y-1.5">
        {[...porSociedad.entries()].map(([id, s]) => (
          <li key={id} className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-xs font-medium" title={s.nombre}>
              {s.nombre}
            </span>
            <span className="code text-2xs text-muted-foreground">
              {s.meses.slice(0, 6).join(", ")}
              {s.meses.length > 6 ? ` y ${s.meses.length - 6} más` : ""}
            </span>
            <Badge variant="outline">{s.docs} comprobantes</Badge>
            {puedeEditar ? (
              <Button
                size="xs"
                disabled={abrir.isPending}
                onClick={() => abrir.mutate(id)}
              >
                <CalendarPlus />
                {t("Abrir los")}{" "}{s.meses.length}{" "}{t("que faltan")}</Button>
            ) : null}
          </li>
        ))}
      </ul>
    </WorkPanel>
  )
}

function Abrir({ sociedades }: { sociedades: { id: string; razon_social: string; pais: string }[] }) {
  const [entityId, setEntityId] = React.useState("")
  const [desde, setDesde] = React.useState("")
  const [hasta, setHasta] = React.useState("")
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const abrir = useMutation({
    mutationFn: () =>
      abrirPeriodos({ entity_id: entityId, desde, hasta, motivo }),
    onSuccess: (r) => {
      toast({
        title: `${r.abiertos} abiertos, ${r.ya_existian} ya existían`,
        description: r.nota ?? undefined,
      })
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["periodos"] })
    },
    onError: onWriteError("los períodos", "abrir"),
  })

  const mesValido = (v: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v)
  const listo =
    entityId !== "" &&
    mesValido(desde) &&
    mesValido(hasta) &&
    desde <= hasta &&
    motivo.trim().length >= 3

  return (
    <WorkPanel
      title={t("Abrir un rango")}
      footnote={t("Abrir un período de más es abrir una puerta que nadie pidió, así que el rango se declara. Si lo que quieres es cubrir lo que ya subiste, usa el botón de arriba: el rango lo dicen tus comprobantes.")}
      bodyClassName="p-2.5"
    >
      <div className="grid gap-2 md:grid-cols-4">
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="per-sociedad">{t("Sociedad")}</Label>
          <select
            id="per-sociedad"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className={cn(
              "h-7 w-full rounded-md border bg-surface px-2 text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <option value="">{t("Elige la sociedad…")}</option>
            {sociedades.map((s) => (
              <option key={s.id} value={s.id}>
                {s.razon_social} · {s.pais}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="per-desde">{t("Desde")}</Label>
          <Input
            id="per-desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            placeholder="2026-01"
            className="code h-7 text-xs"
            aria-invalid={desde !== "" && !mesValido(desde)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="per-hasta">{t("Hasta")}</Label>
          <Input
            id="per-hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            placeholder="2026-12"
            className="code h-7 text-xs"
            aria-invalid={hasta !== "" && !mesValido(hasta)}
          />
        </div>
      </div>
      <div className="mt-2 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <div className="space-y-1">
          <Label htmlFor="per-motivo">{t("Motivo *")}</Label>
          <Textarea
            id="per-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            className="text-xs"
            placeholder={t("Por qué se abren. Queda en la pista de auditoría.")}
          />
        </div>
        <Button size="sm" disabled={!listo || abrir.isPending} onClick={() => abrir.mutate()}>
          <CalendarPlus />
          {abrir.isPending ? "Abriendo…" : "Abrir períodos"}
        </Button>
      </div>
    </WorkPanel>
  )
}
